









import { Component, Prop, Vue } from 'vue-property-decorator';

import VueApexCharts from 'vue-apexcharts';
import { ApexOptions } from 'apexcharts';
import { ResultSet, TCubeMeasure } from '@cubejs-client/core';
import { AvailableChartTypes, isApexChartType } from '@/chartHelper';
import { getChartProps, IChartOptions } from './resultSetTransform';

import { authorizationService } from '@/container';
import { getDefaultChartConfig, IChartConfig, IChartResult } from './resultSetTransform/chartSteps';
import { isArray, isNil, merge } from 'lodash';

@Component({
	components: {
		VueApexCharts
	}
})
export default class CubeJsChart extends Vue {

	@Prop({ type: String, default: '350' }) height!: string | number;
	@Prop({ type: String, default: '100%' }) width!: string | number;
	@Prop({ type: String, default: 'line' }) chartType!: AvailableChartTypes;
	@Prop({ type: String, default: null }) chartTitle!: string | null;

	@Prop({ type: Object, default: null }) resultSet!: ResultSet | null;
	@Prop({ type: Array, default: () => ([]) }) config!: IChartConfig | IChartConfig[];
	@Prop({ type: Object, default: () => ({}) }) chartProps!: ApexOptions;
	@Prop({ type: Boolean, default: false }) groupingByWeek!: boolean;

	defaultChartData: {
		options: ApexOptions
	} = {
			options: {
				chart: {
					toolbar: {
						show: false
					}
				},
				xaxis: {
					labels: {
						trim: true
					}
				}
			}
		};

	defaultConfig: IChartConfig = getDefaultChartConfig();
	// remove it in the future
	internalMeasures: TCubeMeasure[] = [];

	defaultColor: string[] = ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0'];

	reloadKey = 0;

	get internalOptions() {
		const user = authorizationService.getCurrentUser();
		const languageCode = user.languageCode.toLowerCase();

		const config: IChartConfig | IChartConfig[] = Array.isArray(this.config)
			? this.config.map(this.normaliseConfig)
			: this.normaliseConfig(this.config);

		const options: IChartOptions = {
			apexOptions: _.merge({},
				this.defaultChartData.options,
				this.chartProps
			),
			chartType: this.chartType,
			config,
			languageCode,
			translate: this.$t
		};
		return options;
	}

	get internalChartProps() {
		const r = getChartProps(this.resultSet!, this.internalOptions, this.groupingByWeek);

		if (!isNil(r.options.chart)) {
			let dataPointSelection = (event, chartContext, options) => {
				if (isNil(this.resultSet) || !this.resultSet.hasOwnProperty('0'))
					return;
				let results = this.resultSet[0]?.loadResponse?.results[0];
				if (results && results.data) {
					// Get dimension type information from annotations
					const annotations = results.annotation?.dimensions || {};
					
					const dimensionsValues: { [key: string]: string }[] = results.data.map((dataItem) => {
						const dimensions: string[] = this.config[0].axisDimensions.map(x => x.name);
						const dimensionValues: { [key: string]: string } = {};
						dimensions.forEach((dimension) => {
							let value = dataItem[dimension];
							
							// Convert boolean values to 1/0 for SQL compatibility
							// Check if the dimension is of type boolean
							const dimensionType = annotations[dimension]?.type;
							if (dimensionType === 'boolean') {
								// Convert true/false (boolean or string) to 1/0
								if (value === true || value === 'true' || value === 1 || value === '1') {
									value = '1' as string;
								} else if (value === false || value === 'false' || value === 0 || value === '0') {
									value = '0' as string;
								}
								// console.log(value);
							}
							
							dimensionValues[dimension] = value;
						});
						return dimensionValues;
					});
					this.$emit('update:globalFilter', dimensionsValues[options.dataPointIndex]);
				}
			};
			r.options.chart!.events = { ...r.options.chart!.events, dataPointSelection };
		}


		this.computeAndEmitLegend(r);

		return r;
	}


	normaliseConfig(config: IChartConfig): IChartConfig {
		return merge({}, this.defaultConfig, config);
	}

	created() {
		this.fixPercentageIssue();
	}

	computeAndEmitLegend(result: IChartResult) {

		const colors = result.options && result.options.colors;
		let legend = {};
		if (checkArrayType(result.series, Number)) {
			if (isNil(result) || isNil(result.options) || isNil(result.options.labels) || !isArray(result.options.labels))
				legend = {};
			else {

				legend = (result.options.labels as any).map((l, index) => {
					const extractedColor = this.extractColor(colors, index);
					return {
						name: l,
						nameCode: null,
						measures: null,
						color: extractedColor,
						queryIndex: 0
					};
				});
			}
		} else {
			legend = (result.series as any).map((s, index) => {
				const extractedColor = this.extractColor(colors, index);
				return {
					name: s.name,
					nameCode: s.nameCode,
					measures: s.measure,
					color: extractedColor,
					queryIndex: !isNil(result) && !isNil(result.queryIndexes) && isArray(result.queryIndexes) && result.queryIndexes.length > 0 ? result.queryIndexes[index] : 0
				};
			});
		}
		this.$emit('update:legend', legend);

		function checkArrayType(arr, type) {
			if (!Array.isArray(arr)) {
				return false;
			}
			else if (type !== null && type !== undefined && typeof type != 'function') {
				return false;
			}

			// 'null' and 'undefined' require a different check than the main one
			if (type === null || type === undefined) {
				return arr.every(function (e) {
					return e === type;
				});
			}

			return arr.every(function (e) {
				/*
				* not using 'instanceof' because it would return 'true'
				* for cases like 'subObj instanceof Object'.
				* ---------------------------------
				* using 'Object(e)' so that the check works for primitives, see:
				* https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object
				*/
				return Object.getPrototypeOf(Object(e)) === type.prototype;
			});

		}

	}

	extractColor(colors, i) {
		if (isNil(colors) || !isArray(colors) || colors.length < i)
			return { hex: this.defaultColor[getDefaultColorIndex(i)], index: 0 };// TODOS: is 0 right§?
		return colors[i];


		function getDefaultColorIndex(i) {
			const multiplier = Math.floor(i / 5);
			const subtraction = multiplier * 5;
			return i - subtraction;
		}
	}

	fixPercentageIssue() {
		this.$watch(() => this.ChartStackType, (ChartStackType, oldValue) => {
			if (oldValue !== this.ChartStackType)
				this.reloadKey++;
		}, { immediate: true });
	}

	get isSelectedChartTypeForApexChart() {
		return isApexChartType(this.chartType);
	}

	get ChartStackType() {

		if (_.isNil(this.internalChartProps) || _.isNil(this.internalChartProps.options)
			|| _.isNil(this.internalChartProps.options.chart) || _.isNil(this.internalChartProps.options.chart.stackType))
			return null;

		return this.internalChartProps.options.chart.stackType;
	}

	updateReloadKey(chartProps: any) {
		this.config = chartProps;
		this.reloadKey++;
	}

}

